import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Users, Plus, Link2, Unlink, RefreshCw, ShieldCheck,
  ShieldAlert, Trash2, Pencil, X, Check,
} from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { personasAPI } from '../services/api';

/**
 * Página /personas
 *
 * Persona é a identidade que fala por VÁRIOS números. Hoje um canal é uma
 * instância com um nome; com o pool, esse nome passa a ser representado por N
 * números e a campanha escolhe a persona.
 *
 * A tela mostra qualidade e tier de cada número porque é o que decide se ele
 * pode disparar: número em RED ou com tier baixo é o gargalo real, não a
 * quantidade de números.
 */
const Personas = () => {
  const { darkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [personas, setPersonas] = useState([]);
  const [selecionada, setSelecionada] = useState(null);
  const [numeros, setNumeros] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);

  const [criando, setCriando] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [nomeEdicao, setNomeEdicao] = useState('');
  const [vinculando, setVinculando] = useState('');
  const [rotulo, setRotulo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const card = darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200';
  const texto = darkMode ? 'text-white' : 'text-gray-900';
  const sub = darkMode ? 'text-gray-400' : 'text-gray-600';
  const input = darkMode
    ? 'bg-zinc-900 border-gray-700 text-white'
    : 'bg-white border-gray-300 text-gray-900';

  const carregarPersonas = useCallback(async () => {
    try {
      const { data } = await personasAPI.list();
      setPersonas(data || []);
    } catch {
      toast.error('Erro ao carregar personas');
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarDetalhe = useCallback(async (id) => {
    try {
      const [{ data: det }, { data: disp }] = await Promise.all([
        personasAPI.get(id),
        personasAPI.availableInstances(),
      ]);
      setSelecionada(det.persona);
      setNumeros(det.numeros || []);
      setDisponiveis(disp || []);
    } catch {
      toast.error('Erro ao carregar detalhe da persona');
    }
  }, []);

  useEffect(() => { carregarPersonas(); }, [carregarPersonas]);

  const criar = async () => {
    const nome = nomeNovo.trim();
    if (!nome) return toast.warn('Informe o nome da persona');
    setOcupado(true);
    try {
      const { data } = await personasAPI.create({ nome });
      toast.success('Persona criada');
      setNomeNovo('');
      setCriando(false);
      await carregarPersonas();
      await carregarDetalhe(data.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao criar persona');
    } finally {
      setOcupado(false);
    }
  };

  const salvarNome = async (id) => {
    const nome = nomeEdicao.trim();
    if (!nome) return toast.warn('O nome não pode ficar vazio');
    try {
      await personasAPI.update(id, { nome });
      toast.success('Persona atualizada');
      setEditandoId(null);
      await carregarPersonas();
      if (selecionada?.id === id) setSelecionada({ ...selecionada, nome });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao atualizar');
    }
  };

  const desativar = async (id) => {
    // Desativar não apaga: campanhas históricas referenciam a persona.
    if (!window.confirm('Desativar esta persona? Campanhas antigas continuam com o histórico.')) return;
    try {
      await personasAPI.deactivate(id);
      toast.success('Persona desativada');
      if (selecionada?.id === id) setSelecionada(null);
      await carregarPersonas();
    } catch {
      toast.error('Erro ao desativar');
    }
  };

  const vincular = async () => {
    if (!vinculando) return toast.warn('Escolha um número');
    setOcupado(true);
    try {
      const { data } = await personasAPI.linkInstance(selecionada.id, vinculando, rotulo || undefined);
      setNumeros(data.numeros || []);
      setVinculando('');
      setRotulo('');

      // O retorno diz o que aconteceu com os templates. WABA compartilhada só
      // registra (pulados); WABA diferente submete e entra em aprovação.
      const t = data.templates || {};
      if (t.motivo) toast.info(t.motivo);
      else if (t.submetidos) toast.success(`Número vinculado. ${t.submetidos} template(s) submetido(s) à Meta.`);
      else if (t.pulados) toast.success(`Número vinculado. ${t.pulados} template(s) já disponíveis nesta WABA.`);
      else toast.success('Número vinculado');

      if (t.erros?.length) toast.warn(`${t.erros.length} template(s) com erro. Use "Sincronizar templates".`);

      await carregarPersonas();
      const { data: disp } = await personasAPI.availableInstances();
      setDisponiveis(disp || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao vincular número');
    } finally {
      setOcupado(false);
    }
  };

  const desvincular = async (instanciaId) => {
    try {
      await personasAPI.unlinkInstance(instanciaId);
      toast.success('Número desvinculado');
      await carregarDetalhe(selecionada.id);
      await carregarPersonas();
    } catch {
      toast.error('Erro ao desvincular');
    }
  };

  const sincronizar = async (instanciaId) => {
    try {
      const { data } = await personasAPI.syncTemplates(selecionada.id, instanciaId);
      if (data.motivo) toast.info(data.motivo);
      else toast.success(`${data.submetidos} submetido(s), ${data.pulados} já existente(s)`);
      if (data.erros?.length) toast.warn(`${data.erros.length} template(s) com erro`);
    } catch {
      toast.error('Erro ao sincronizar templates');
    }
  };

  /** Qualidade é o sinal que decide se o número pode disparar. */
  const badgeQualidade = (q) => {
    const mapa = {
      GREEN: { txt: 'Boa', cls: 'bg-green-100 text-green-700', Icone: ShieldCheck },
      YELLOW: { txt: 'Média', cls: 'bg-yellow-100 text-yellow-700', Icone: ShieldAlert },
      RED: { txt: 'Ruim', cls: 'bg-red-100 text-red-700', Icone: ShieldAlert },
    };
    const m = mapa[q];
    if (!m) return <span className={`text-xs ${sub}`}>—</span>;
    const { Icone } = m;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
        <Icone className="h-3 w-3" /> {m.txt}
      </span>
    );
  };

  const formatarTier = (t) => (t ? String(t).replace('TIER_', '').replace('_', ' ') : '—');

  return (
    <Layout>
      <PageHeader
        title="Personas de Disparo"
        subtitle="Uma identidade, vários números. A campanha escolhe a persona e o sistema distribui entre os números dela."
        icon={Users}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de personas */}
        <div className={`rounded-lg border p-4 ${card}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`font-semibold ${texto}`}>Personas</h2>
            <button
              onClick={() => setCriando(!criando)}
              className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-nomoz-orange text-white"
            >
              <Plus className="h-4 w-4" /> Nova
            </button>
          </div>

          {criando && (
            <div className="mb-4 flex gap-2">
              <input
                autoFocus
                value={nomeNovo}
                onChange={(e) => setNomeNovo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && criar()}
                placeholder="Ex.: Leci e Orlando"
                className={`flex-1 px-3 py-2 rounded-md border text-sm ${input}`}
              />
              <button onClick={criar} disabled={ocupado} className="px-3 rounded-md bg-green-600 text-white disabled:opacity-50">
                <Check className="h-4 w-4" />
              </button>
            </div>
          )}

          {loading ? (
            <p className={`text-sm ${sub}`}>Carregando…</p>
          ) : personas.length === 0 ? (
            <p className={`text-sm ${sub}`}>
              Nenhuma persona ainda. Crie uma e vincule os números que vão falar por ela.
            </p>
          ) : (
            <ul className="space-y-1">
              {personas.map((p) => (
                <li key={p.id}>
                  <div
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left ${
                      selecionada?.id === p.id
                        ? 'bg-nomoz-orange/10 border border-nomoz-orange/40'
                        : darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    {editandoId === p.id ? (
                      <>
                        <input
                          autoFocus
                          value={nomeEdicao}
                          onChange={(e) => setNomeEdicao(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && salvarNome(p.id)}
                          className={`flex-1 px-2 py-1 rounded border text-sm ${input}`}
                        />
                        <button onClick={() => salvarNome(p.id)} className="text-green-600" title="Salvar">
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setEditandoId(null)} className={sub} title="Cancelar">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => carregarDetalhe(p.id)} className="flex-1 text-left">
                          <span className={`text-sm font-medium ${texto} ${!p.ativo ? 'line-through opacity-60' : ''}`}>
                            {p.nome}
                          </span>
                          <span className={`block text-xs ${sub}`}>
                            {p.total_numeros} número(s){!p.ativo && ' · inativa'}
                          </span>
                        </button>
                        <button
                          onClick={() => { setEditandoId(p.id); setNomeEdicao(p.nome); }}
                          className={sub} title="Renomear"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => desativar(p.id)} className="text-red-500" title="Desativar">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detalhe */}
        <div className={`lg:col-span-2 rounded-lg border p-4 ${card}`}>
          {!selecionada ? (
            <p className={`text-sm ${sub}`}>Selecione uma persona para ver e gerenciar os números.</p>
          ) : (
            <>
              <h2 className={`font-semibold mb-1 ${texto}`}>{selecionada.nome}</h2>
              <p className={`text-sm mb-4 ${sub}`}>
                Ao vincular um número, os templates da persona são registrados nele automaticamente.
              </p>

              {/* Vincular */}
              <div className={`rounded-md border p-3 mb-5 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-sm font-medium mb-2 ${texto}`}>Vincular número</p>
                {disponiveis.length === 0 ? (
                  <p className={`text-xs ${sub}`}>
                    Nenhum número livre. Um número pertence a uma persona só — desvincule antes de mover.
                  </p>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={vinculando}
                      onChange={(e) => setVinculando(e.target.value)}
                      className={`flex-1 px-3 py-2 rounded-md border text-sm ${input}`}
                    >
                      <option value="">Escolha um número…</option>
                      {disponiveis.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.nome_instancia} {i.numero_whatsapp ? `— ${i.numero_whatsapp}` : ''} ({i.provider})
                        </option>
                      ))}
                    </select>
                    <input
                      value={rotulo}
                      onChange={(e) => setRotulo(e.target.value)}
                      placeholder="Rótulo interno (opcional)"
                      className={`px-3 py-2 rounded-md border text-sm ${input}`}
                    />
                    <button
                      onClick={vincular}
                      disabled={ocupado}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md bg-nomoz-orange text-white text-sm disabled:opacity-50"
                    >
                      <Link2 className="h-4 w-4" /> Vincular
                    </button>
                  </div>
                )}
              </div>

              {/* Números */}
              {numeros.length === 0 ? (
                <p className={`text-sm ${sub}`}>Nenhum número vinculado ainda.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`text-left ${sub}`}>
                        <th className="py-2 pr-3 font-medium">Número</th>
                        <th className="py-2 pr-3 font-medium">Rótulo</th>
                        <th className="py-2 pr-3 font-medium">Situação</th>
                        <th className="py-2 pr-3 font-medium">Qualidade</th>
                        <th className="py-2 pr-3 font-medium">Tier</th>
                        <th className="py-2 pr-3 font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {numeros.map((n) => (
                        <tr key={n.id} className={darkMode ? 'border-t border-gray-700' : 'border-t border-gray-200'}>
                          <td className={`py-2 pr-3 ${texto}`}>
                            {n.nome_instancia}
                            <span className={`block text-xs ${sub}`}>{n.numero_whatsapp || '—'}</span>
                          </td>
                          <td className={`py-2 pr-3 ${sub}`}>{n.rotulo_interno || '—'}</td>
                          <td className={`py-2 pr-3 ${sub}`}>{n.status || '—'}</td>
                          <td className="py-2 pr-3">{badgeQualidade(n.qualidade)}</td>
                          <td className={`py-2 pr-3 ${sub}`}>{formatarTier(n.messaging_limit)}</td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => sincronizar(n.id)}
                                className={sub}
                                title="Sincronizar templates neste número"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => desvincular(n.id)}
                                className="text-red-500"
                                title="Desvincular da persona"
                              >
                                <Unlink className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Personas;
